import { DateTime } from '@ksyos/date-time';
import { Alert } from '@ksyos/design-system';
import type { EpisodeCategory } from '@common/enums';
import type { IWithRouterProps } from '@frontend/components/elements/HoC/withRouter';
import { PatientDetailsChangeSource } from '@common/enums/patientDetailsChangeSource';
import { withRouter } from '@frontend/components/elements/HoC/withRouter';
import { getIsFlexible } from '@frontend/services/api/episodeCategoriesApiService';
import type { IPatient } from '../../../../common/interfaces/patient';
import type { ValidationErrors } from '../../../utils/validation';
import type { IInsuranceHistory } from '../../interfaces/insuranceHistory';
import { getDisplayName } from '../../../../common/utils/getDisplayName';
import { getFormattedAddress } from '../../../../common/utils/getFormattedAddress';
import { patientUpdateSchema } from '../../../../common/validation/patient';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { emptyValidationErrors, validate } from '../../../utils/validation';
import {
    ButtonContainer,
    FormContainer,
    FormDivider,
    FormGroup,
    FormLabel,
    FormParagraph,
    FullWidthInputGroup,
    Header,
    Icon,
    IconColor,
    InputCheckbox,
    InputTextArea,
    Loading,
    SaveButton,
    SubHeader,
} from '../../elements/common';
import { Component } from '../../elements/Component';
import { HorizontalFormGroup } from '../../elements/forms/HorizontalFormGroup';
import { FixedWidthTemplate } from '../../templates/FixedWidthTemplate';

type PatientSelectOrAddProps = IWithRouterProps;

interface IPatientSelectOrAddState {
    activeInsurance?: IInsuranceHistory;
    dataChecked: boolean;
    errors: ValidationErrors;
    identifierSocialSecurity: string | null;
    isFlexibleCarePath?: boolean;
    patient?: IPatient;
}

class InnerPatientSelectOrAdd extends Component<PatientSelectOrAddProps, IPatientSelectOrAddState> {
    private readonly abortController = new AbortController();

    constructor(props: PatientSelectOrAddProps) {
        super(props);

        this.state = {
            dataChecked: false,
            errors: emptyValidationErrors,
            identifierSocialSecurity: null,
        };
    }

    public async componentDidMount() {
        await this.loadPatient();
        await this.loadActiveInsurance();
        await this.loadIsFlexibleCarePath();
    }

    public componentWillUnmount() {
        this.abortController.abort();
    }

    public render() {
        return (
            <FixedWidthTemplate>
                <Header content={translateKey('patient.patientSelectOrAdd')} />
                <Loading
                    item={this.state.patient}
                    loaded={(patient) => (
                        <FormContainer onSubmit={async () => null}>
                            {!patient.identifierSocialSecurity && (
                                <Alert appearance="warning">
                                    <Icon
                                        color={IconColor.danger}
                                        icon="allergy"
                                    />{' '}
                                    {translateKey('patient.noBSNPleaseCheckData')}
                                </Alert>
                            )}

                            <HorizontalFormGroup>
                                <SubHeader text={getDisplayName(patient)} />
                            </HorizontalFormGroup>

                            {this.renderRow(
                                'common.birthDate',
                                'birthDate',
                                patient.birthDate ? DateTime.from(patient.birthDate).format('DD-MM-YYYY') : ''
                            )}
                            {this.renderRow(
                                'common.gender',
                                'gender',
                                patient.gender && translateKey(`genderEnum.${patient.gender}`)
                            )}
                            {this.renderRow(
                                'common.healthInsurer',
                                'healthInsurer',
                                this.state.activeInsurance &&
                                    this.state.activeInsurance.insurer &&
                                    this.state.activeInsurance.insurer.name
                            )}
                            {this.renderRow(
                                'patient.insurancePolicyNumber',
                                'policyNumber',
                                this.state.activeInsurance && this.state.activeInsurance.insurancePolicyNumber
                            )}
                            {this.renderRow('common.streetAndNumber', 'street', getFormattedAddress(patient))}
                            {this.renderRow('common.zipcode', 'zip', patient.zip)}
                            {this.renderRow('common.city', 'city', patient.city)}
                            {this.renderRow('common.homeNumber', 'homePhoneNumber', patient.homePhoneNumber)}
                            {this.renderRow('common.mobileNumber', 'mobileNumber', patient.mobileNumber)}
                            {this.renderRow(
                                'patient.identifierSocialSecurity',
                                'identifierSocialSecurity',
                                patient.identifierSocialSecurity
                            )}

                            <InputCheckbox
                                checked={this.state.dataChecked}
                                onChange={async () => {
                                    await this.setState({ dataChecked: !this.state.dataChecked });
                                }}
                                text={translateKey('patient.iHaveCheckedTheData')}
                            />

                            {!patient.identifierSocialSecurity && (
                                <>
                                    <FormDivider />
                                    <FormGroup>
                                        <FormParagraph bold>
                                            {translateKey('common.ifKnown') +
                                                ', ' +
                                                translateKey(
                                                    'common.enterAnItem',
                                                    translateKey('patient.identifierSocialSecurity')
                                                )}
                                        </FormParagraph>
                                    </FormGroup>

                                    <FormGroup hasErrors={this.state.errors.has('identifierSocialSecurity')}>
                                        <FullWidthInputGroup>
                                            <FormLabel> {translateKey('patient.identifierSocialSecurity')} </FormLabel>
                                            <InputTextArea
                                                error={this.state.errors.get('identifierSocialSecurity')}
                                                onChange={async (value) => {
                                                    await this.setState({ identifierSocialSecurity: value });
                                                }}
                                                onDoValidate={async () => {
                                                    return;
                                                }}
                                                placeholder={
                                                    translateKey(
                                                        'common.enterAnItem',
                                                        translateKey('patient.identifierSocialSecurity')
                                                    ) + '…'
                                                }
                                                value={this.state.identifierSocialSecurity}
                                            />
                                        </FullWidthInputGroup>
                                    </FormGroup>
                                </>
                            )}
                        </FormContainer>
                    )}
                />

                <ButtonContainer
                    alignRight
                    buttons={[
                        <SaveButton
                            name={'patient.createNew'}
                            onClick={() =>
                                this.props.router.navigate(`/patients/add/sso/${this.props.router.params.ssoId}`)
                            }
                        />,
                        <SaveButton
                            disabled={!this.state.dataChecked}
                            name={'patient.proceed'}
                            onClick={async () => await this.onSave()}
                        />,
                    ]}
                />
            </FixedWidthTemplate>
        );
    }

    private renderRow(name: string, key: string, patientValue?: string | null) {
        if (!patientValue || patientValue === null || patientValue === '') {
            return;
        }

        return (
            <FormGroup key={key}>
                <FormLabel>{translateKey(`${name}`)}</FormLabel>
                <FormParagraph bold>{patientValue}</FormParagraph>
            </FormGroup>
        );
    }

    private async loadPatient() {
        const url = `/api/patients/${this.props.router.params.patientId}`;
        const result = await fetchJson(url, 'GET');
        await this.setState({ patient: result.json });
    }

    private async loadActiveInsurance() {
        const url = `/api/patients/${this.props.router.params.patientId}/insuranceHistory?isActive=true`;
        const result = await fetchJson(url, 'GET');

        if (!result.json || !result.json.items || result.json.items.length === 0) {
            return;
        }

        await this.setState({ activeInsurance: result.json.items[0] });
    }

    private async loadIsFlexibleCarePath() {
        const forwardToEpisode = this.props.router.searchParams.get('forwardToEpisode');

        if (forwardToEpisode) {
            const result = await getIsFlexible(
                forwardToEpisode as EpisodeCategory,
                this.props.router.params.patientId,
                this.abortController.signal
            );

            await this.setState({ isFlexibleCarePath: result });
        }
    }

    private async onSave() {
        const navigate = this.props.router.navigate;
        const forwardToEpisode = this.props.router.searchParams.get('forwardToEpisode');
        const forwardToMerge = this.props.router.searchParams.get('forwardToMerge');
        const ssoId = this.props.router.params.ssoId;
        const patientId = this.props.router.params.patientId;
        if (!this.state.patient) {
            return;
        }

        if (!this.state.patient.identifierSocialSecurity && this.state.identifierSocialSecurity) {
            const { item, errors } = validate(patientUpdateSchema, {
                ...this.state.patient,
                identifierSocialSecurity: this.state.identifierSocialSecurity,
            });
            await this.setState({ errors });
            if (errors.size > 0) {
                return;
            }

            const params = new URLSearchParams({
                patientDetailsChangeSource: PatientDetailsChangeSource.sso,
                ...(ssoId && { ssoId: ssoId }),
            });

            const url = `/api/patients/${patientId}?${params.toString()}`;

            await fetchJson(url, 'POST', item);
        }

        if (forwardToEpisode) {
            if (forwardToMerge === 'true') {
                const params = new URLSearchParams({
                    forwardToEpisode: forwardToEpisode,
                });
                navigate(`/patients/${patientId}/mergesso/${ssoId}?${params.toString()}`);
            } else {
                if (this.state.isFlexibleCarePath) {
                    navigate(`/patients/${patientId}/${forwardToEpisode}`);
                } else {
                    const params = new URLSearchParams({
                        episodeCategory: forwardToEpisode,
                        patientId: patientId,
                    });
                    navigate(`/episode?${params.toString()}`);
                }
            }
        } else {
            if (forwardToMerge === 'true') {
                navigate(`/patients/${patientId}/mergesso/${ssoId}`);
            } else {
                this.props.router.navigate(`/patients/${this.props.router.params.patientId}`);
            }
        }
    }
}

export const PatientSelectOrAdd = withRouter(InnerPatientSelectOrAdd);
